import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { InspectionItem, InspectionResultEnum } from '../../types/inspection.type';
import { LoadingController, ModalController, IonicModule } from '@ionic/angular';
import { environment } from '../../../environments/environment';

import { AutomatedClickThrough } from '../../shared/automated-click-through';
import { NonServicePeriod } from '../../types/non-service-period.type';
import { TranslateService } from '../../services/translate.service';
import { AppData } from 'src/app/data';
import { NotificationService } from '../../services/notification.service';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-inspection',
  templateUrl: './inspection.page.html',
  styleUrls: ['./inspection.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonicModule, NgTemplateOutlet, NgClass, TranslatePipe],
})
export class InspectionPage extends AutomatedClickThrough implements OnInit {
  @Input() onComplete: (success: boolean, inspectionItems: InspectionItem[]) => Promise<void>;
  @Input() nonServicePeriod: NonServicePeriod;

  public state: 'inspection' | 'criticalWarning' = 'inspection';

  public readonly data = inject(AppData);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly modalCtrl = inject(ModalController);
  private readonly translateService = inject(TranslateService);
  private readonly notify = inject(NotificationService);

  private readonly gtm = (<any>window).dataLayer || [];

  public canSubmit = false;
  public itemsComplete = 0;
  public criticalItemsLeft = 0;
  public features = environment.features;

  // Making the enum available to the template
  InspectionResultEnum = InspectionResultEnum;
  inspectionItems: InspectionItem[] = [];
  inspectionGroups: {
    serviceCategory: string
    inspectionItems: InspectionItem[]
  }[] = [];

  ngOnInit() {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'view_ view_inspection' });
    let ungroupedInspectionItems: InspectionItem[] = [];
    this.inspectionItems = this.nonServicePeriod.inspectionItems || [];

    const inspectionGroupMap: { [serviceCategory: string]: InspectionItem[] } = {};
    for (const inspectionItem of this.inspectionItems) {
      if (!inspectionItem.serviceCategory) {
        ungroupedInspectionItems.push(inspectionItem);
      } else {
        inspectionGroupMap[inspectionItem.serviceCategory] =
          inspectionGroupMap[inspectionItem.serviceCategory] || [];
        inspectionGroupMap[inspectionItem.serviceCategory].push(inspectionItem);
      }
    }

    for (const key in inspectionGroupMap) {
      this.inspectionGroups.push({
        serviceCategory: key,
        inspectionItems: inspectionGroupMap[key],
      });
      inspectionGroupMap[key] = this.sortAlphabetically(inspectionGroupMap[key], 'description');
    }

    ungroupedInspectionItems = this.sortAlphabetically(ungroupedInspectionItems, 'description');
    this.inspectionGroups = this.sortAlphabetically(this.inspectionGroups, 'serviceCategory');
    if (ungroupedInspectionItems.length)
      this.inspectionGroups.unshift({
        serviceCategory: null,
        inspectionItems: ungroupedInspectionItems,
      });

    this.itemsComplete = this.getInspectedItemsCount();
    this.criticalItemsLeft = this.getCriticalItemsLeftCount();
    this.canSubmit = this.isInspectionCompleted();
  }

  automateClickThrough(): void {
    this.submitInspection();
  }

  public inspectionValueClicked(event: any, inspection: InspectionItem): void {
    inspection.result = Number(event.detail.value);
    this.canSubmit = this.isInspectionCompleted();
    this.itemsComplete = this.getInspectedItemsCount();
    this.criticalItemsLeft = this.getCriticalItemsLeftCount();
  }

  public inspectionValueChanged(inspection: InspectionItem, value: string): void {
    inspection.textResult = value;
    this.itemsComplete = this.getInspectedItemsCount();
    this.criticalItemsLeft = this.getCriticalItemsLeftCount();
    this.canSubmit = this.isInspectionCompleted();
  }

  public inspectionCommentChanged(inspection: InspectionItem, value: string): void {
    inspection.comment = value;
  }

  private isInspectionCompleted(): boolean {
    return !(this.inspectionItems || []).some(item =>
      this.features.inspection.nonCriticalOptional
        ? item.isCritical && !item.result && !item.textResult
        : !item.result && !item.textResult,
    );
  }

  private getInspectedItemsCount(): number {
    return (this.inspectionItems || []).filter(item => item.result || item.textResult).length;
  }

  private getCriticalItemsLeftCount(): number {
    const criticalItems = (this.inspectionItems || []).filter(item => item.isCritical);
    return (
      criticalItems.length - criticalItems.filter(item => item.result || item.textResult).length
    );
  }

  public async submitInspection(): Promise<boolean> {
    const loading = await this.loadingCtrl.create({
      spinner: 'crescent',
      message: this.translateService.translate('LABEL.finishingInsp'),
      backdropDismiss: false,
    });
    await loading.present();
    const failuresExist = this.inspectionItems.some(
      item =>
        item.isCritical &&
        item.result ===
          (environment.features.inspection.alternateResultValues
            ? InspectionResultEnum.ServiceDueSoon
            : InspectionResultEnum.ServiceCritical),
    );

    if (failuresExist) {
      await loading.dismiss();
      this.state = 'criticalWarning';
      return false;
    }

    // Keep loading visible during API call
    await this.onComplete(!failuresExist, this.inspectionItems);

    // Dismiss loading before closing modal
    await loading.dismiss();

    // Close modal
    return this.modalCtrl.dismiss({
      success: !failuresExist,
      inspectionItems: this.inspectionItems,
    });
  }

  public async bypassInspection(): Promise<boolean> {
    const loading = await this.loadingCtrl.create({
      spinner: 'crescent',
      message: this.translateService.translate('LABEL.finishingInsp'),
      backdropDismiss: false,
    });
    try {
      await loading.present();
      // mark inspectionItems as NOT_PERFORMED
      this.inspectionItems.forEach(item => {
        item.result = InspectionResultEnum.NotPerformed;
      });
      return await this.submit(false);
    } catch (error) {
      console.error(error);
      this.notify.error(this.translateService.translate('MESSAGE.genericError'));
      return false;
    } finally {
      await loading.dismiss();
    }
  }

  public cancelCriticalWarning(): void {
    this.state = 'inspection';
  }

  public async submit(failuresExist: boolean): Promise<boolean> {
    await this.onComplete(!failuresExist, this.inspectionItems);
    return this.modalCtrl.dismiss({
      success: !failuresExist,
      inspectionItems: this.inspectionItems,
    });
  }

  public getFailures(): InspectionItem[] {
    return this.inspectionItems.filter(
      item =>
        item.isCritical &&
        item.result ===
          (environment.features.inspection.alternateResultValues
            ? InspectionResultEnum.ServiceDueSoon
            : InspectionResultEnum.ServiceCritical),
    );
  }

  private sortAlphabetically(list: any[], key: string) {
    return list.sort((a, b) => {
      const aLowerCase = a[key].toLowerCase();
      const bLowerCase = b[key].toLowerCase();
      if (aLowerCase > bLowerCase) {
        return 1;
      } else if (aLowerCase < bLowerCase) {
        return -1;
      }
      return 0;
    });
  }
}
